"use client";

import { useState } from "react";

import { strings } from "@perceptron/constants";
import { Button } from "@perceptron/ui/components/button";
import { Icon } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";
import { formatString } from "@perceptron/utils/string";
import Image from "next/image";
import Link from "next/link";

import { MODE_META, type Mode, type ModeMeta } from "@demo/constants/mode";

type ModeCardProps = {
  /** The mode this card represents. */
  mode: Mode;
  /** True when this card is interactive: always on desktop (all cards visible), the focused slide on mobile. */
  isActive: boolean;
  /** True when this is the app's currently-active mode. */
  isCurrent: boolean;
  /** Switch the app to this mode. No-op when {@link isCurrent}. */
  onSwitch: () => void;
};

/**
 * Card hero. Plays the mode's looping preview video when it has one, falling
 * back to the textured label image if the mode has no video or the video fails
 * to load.
 */
const CardHero = ({ meta, onClick }: { meta: ModeMeta; onClick: () => void }) => {
  const [videoFailed, setVideoFailed] = useState(false);
  const showVideo = Boolean(meta.previewVideo) && !videoFailed;

  return (
    <div
      className="relative flex aspect-[326/187] w-full transform-gpu flex-col items-center justify-center overflow-hidden rounded-2xl border border-background-alt p-4"
      role="button"
      onClick={onClick}
    >
      {showVideo ? (
        <video
          aria-hidden
          autoPlay
          className="absolute inset-0 h-full w-full object-cover"
          disablePictureInPicture
          disableRemotePlayback
          loop
          muted
          playsInline
          preload="metadata"
          src={meta.previewVideo}
          onError={() => setVideoFailed(true)}
        />
      ) : (
        <>
          <Image
            alt=""
            className="object-cover"
            fill
            priority={false}
            sizes="350px"
            src="/demo/assets/images/mode-card-texture.png"
          />
          <div aria-hidden className="absolute inset-0 bg-[rgba(213,136,79,0.06)]" />
          <p className="relative whitespace-nowrap text-center font-ppmondwest text-5xl leading-normal text-white">
            {meta.label}
          </p>
        </>
      )}
    </div>
  );
};

/**
 * A single mode card: hero (preview video or textured label), a title +
 * "Docs" link, a description, and a switch/active button. Width is driven by
 * the carousel slide, so the card fills its slot (`w-full h-full`).
 */
const ModeCard = ({ mode, isActive, isCurrent, onSwitch }: ModeCardProps) => {
  const meta = MODE_META[mode];

  return (
    <div
      className={cn(
        "flex h-full w-full select-none flex-col items-start gap-6 rounded-2xl border border-background-alt bg-background-alt-2 px-5 py-4 drop-shadow-[0px_4px_2px_rgba(0,0,0,0.07)]",
        {
          "pointer-events-none": !isActive,
        },
      )}
    >
      <CardHero meta={meta} onClick={onSwitch} />

      <div className="flex w-full flex-col gap-1">
        <div className="flex w-full items-center gap-2">
          <span className="flex-1 text-base font-semibold leading-6 text-foreground">{meta.label}</span>
          <Link
            className="flex shrink-0 items-center gap-1 rounded-sm bg-background px-2 py-1 text-sm font-medium leading-5 text-foreground"
            href={meta.docsUrl}
            rel="noopener noreferrer"
            target="_blank"
            onClick={(e) => e.stopPropagation()}
          >
            <span>{strings.modeSwitcher.docs}</span>
            <Icon className="size-4" name="external-link" />
          </Link>
        </div>
        <p className="h-12 w-full text-base leading-6 text-foreground">{meta.description}</p>
      </div>

      {isCurrent ? (
        <Button className="w-full" disabled size="sm">
          {strings.modeSwitcher.active}
        </Button>
      ) : (
        <Button className="w-full" size="sm" onClick={onSwitch}>
          {formatString(strings.modeSwitcher.switchTo, meta.label)}
        </Button>
      )}
    </div>
  );
};

export default ModeCard;
